import axios from "axios";
import axiosInstance from "../axiosInstance";
import type {
  LoginCredentials,
  LoginResult,
  LoginValidationErrors,
} from "../pages/login/types/login.types";
import { setActionLog } from "../utils/util";

interface LoginApiResponse {
  data: {
    accessToken: string;
    refreshToken: string;
  };
}

/**
 * 로그인 폼 기본 유효성 검사.
 * 비어 있는 필드에 대한 메시지를 반환합니다.
 */
export function validateLoginCredentials(
  credentials: LoginCredentials
): LoginValidationErrors {
  const errors: LoginValidationErrors = {};

  if (!credentials.userId.trim()) {
    errors.userId = "아이디를 입력해 주세요.";
  }
  if (!credentials.password) {
    errors.password = "비밀번호를 입력해 주세요.";
  }

  return errors;
}

/**
 * Spring Boot 인증 API 로그인 요청.
 * POST /api/v1/auth/login → 역할 조회 → 성공 시 토큰·역할 반환
 */
export async function loginRequest(
  credentials: LoginCredentials
): Promise<LoginResult> {
  const { data }: { data: LoginApiResponse } = await axios.post(
    "/api/v1/auth/login",
    JSON.stringify({
      userId: credentials.userId.trim(),
      password: credentials.password,
    }),
    {
      headers: { "Content-Type": "application/json" },
    }
  );

  const tokens = {
    accessToken: data.data.accessToken,
    refreshToken: data.data.refreshToken,
  };

  localStorage.setItem("accessToken", tokens.accessToken);
  localStorage.setItem("refreshToken", tokens.refreshToken);

  const rolesRes = await axiosInstance.get("/api/v1/auth/user/roles");
  const roles = (rolesRes.data?.data as unknown[]) || [];

  if (!roles.length) {
    localStorage.clear();
    throw new Error("NO_ACCESS");
  }

  await setActionLog({ eventType: "LOGIN", title: "로그인" });

  return { tokens, roles };
}

/**
 * 로컬/개발용 mock 로그인 (API 없이 UI 검증 시 사용).
 * 기본 credentials: admin / admin
 */
export async function mockLoginRequest(
  credentials: LoginCredentials
): Promise<LoginResult> {
  await new Promise((resolve) => setTimeout(resolve, 900));

  if (
    credentials.userId.trim() !== "admin" ||
    credentials.password !== "admin"
  ) {
    throw new Error("INVALID_CREDENTIALS");
  }

  const tokens = {
    accessToken: "mock-access-token",
    refreshToken: "mock-refresh-token",
  };

  localStorage.setItem("accessToken", tokens.accessToken);
  localStorage.setItem("refreshToken", tokens.refreshToken);

  return { tokens, roles: [{ name: "ADMIN" }] };
}

export type LoginErrorCode =
  | "INVALID_CREDENTIALS"
  | "NO_ACCESS"
  | "NETWORK"
  | "UNKNOWN";

export function resolveLoginError(error: unknown): {
  code: LoginErrorCode;
  message: string;
} {
  if (error instanceof Error) {
    if (error.message === "NO_ACCESS") {
      return {
        code: "NO_ACCESS",
        message: "접근권한이 없습니다.\n관리자에게 문의 바랍니다.",
      };
    }
    if (error.message === "INVALID_CREDENTIALS") {
      return {
        code: "INVALID_CREDENTIALS",
        message: "아이디 혹은 비밀번호가 맞지 않습니다.",
      };
    }
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 400 || status === 403) {
      return {
        code: "INVALID_CREDENTIALS",
        message: "아이디 혹은 비밀번호가 맞지 않습니다.",
      };
    }
    if (!error.response) {
      return {
        code: "NETWORK",
        message: "서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.",
      };
    }
  }

  return {
    code: "UNKNOWN",
    message: "아이디 혹은 비밀번호가 맞지 않습니다.",
  };
}
